/* eslint-disable import/prefer-default-export */
import axios from "axios";

import { getUserCredentials, getCachedFirebaseToken } from "../utils/common";
import { API_URL } from "../utils/Constant";

/**
 * Get company name
 * @param {String} customerID
 * @return  {Object} {success, contact_name, email, company, address, city, zip, state, telephone}
 */
export const getCustomerInfo = async (customerID) => {
  try {
    const resp = await axios.get(`${API_URL}/customerinfo/`, {
      params: { customer_id: customerID },
    });

    const result = {
      success: resp.data.message === "Success",
      message: resp.data.message,
      ...resp.data.result,
    };

    return result;
  } catch (e) {
    // console.log("error from getCompanyName", e);
    // getViewAsPerStatusCode(e);
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Fetch invite prefill data for onboarding.
 * GET /api/customerinvitation/?unique_invitation_id=<id>
 *
 * @param {String} uniqueInvitationId
 * @returns {Promise<{ success: boolean, message?: string, result?: Array }>}
 */
export const getCustomerInvitation = async (uniqueInvitationId) => {
  try {
    const response = await axios.get(`${API_URL}/customerinvitation/`, {
      params: {
        unique_invitation_id: uniqueInvitationId,
        disableInterceptor: true,
      },
      // Keep expired / not-found bodies in the caller instead of interceptor redirects.
      validateStatus: () => true,
    });

    const data = response.data || {};
    const result = Array.isArray(data.result) ? data.result : [];
    const success = data.message === "Success" && result.length > 0;

    return {
      success,
      message: data.message,
      result: success ? result : [],
    };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message,
      result: [],
    };
  }
};

/**
 * Submit the invite form and send the invitation email to the customer's email address
 * @param {Object} payload
 * @returns {Object} { success, message}
 */
export const sendCustomerInvite = async (payload) => {
  try {
    const {
      sales_email,
      csm_email,
      data_ops_email,
      company_name,
      customer_name,
      customer_email,
      customer_type,
      customer_id,
    } = payload;
    const params = {
      company: company_name,
      customer_name,
      customer_email,
      customer_type,
      sales: sales_email,
      csm: csm_email,
      data_ops: data_ops_email,
      ...(customer_id ? { customer_id } : {}),
    };
    const userCredentials = getUserCredentials();
    const token = getCachedFirebaseToken() || userCredentials?.firebase_id_token;
    if (!token) {
      return { success: false, message: "Authentication required to send invite." };
    }

    const response = await axios.post(`${API_URL}/inviteadmin/`, params, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.data;
    return { success: data.message === "Success", ...data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Submit onboarding form
 * @param {Object} payload
 * @returns {Object} { success, message}
 */
export const submitOnboardingForm = async (payload) => {
  try {
    const resp = await axios.post(`${API_URL}/usersignuponboard/`, {
      ...payload,
    });

    return { success: true, message: resp.data.message };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Submit request form for environment and product
 * @param {Object} payload
 * @returns {Object} { success, message}
 */

export const submitOnboardingFields = async (payload) => {
  try {
    const resp = await axios.post(`${API_URL}/onboardingfields/`, {
      ...payload,
    });

    return { success: true, message: resp.data.message };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Update sample fields, link report fields and link pattern
 * @param {"product" | "environment"} sample_category "product" | "environment"
 * @param {*} updated_sample_fields updated sample fields array
 */
export const updateSampleFields = async (sample_category = "product", updated_sample_fields) => {
  try {
    const payload = {
      sample_category, updated_sample_fields,
    };
    const resp = await axios.patch(`${API_URL}/updatesamplefields/`, payload);
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * get processed data from uploaded excel file
 * @param {"product" | "environment"} specs_type "product" | "environment"
 * @param {string} s3_base_url s3 base url of uploaded file
 * @param {string} specs_excel_s3_path s3 path of uploaded file
 */
export const parseExcelFile = async (specs_type = "product", s3_base_url, specs_excel_s3_path) => {
  try {
    const user_email = getUserCredentials().user_email;
    const resp = await axios.post(`${API_URL}/excelparser/`, {
      specs_type, s3_base_url, specs_excel_s3_path, ml_excel_parse: "True", user_email,
    }, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Insert data to database after excel upload and parse
 * @param {"product" | "environment"} specs_type "product" | "environment"
 * @param {*} data_tb_inserted data coming from api/insertexceldata/
 */
export const insertExcelDataToDb = async (specs_type, data_tb_inserted) => {
  try {
    const resp = await axios.post(`${API_URL}/insertexceldata/`, {
      specs_type, data_tb_inserted,
    });
    return { success: true, ...resp.data };
  } catch (e) {
    return { success: false, message: e.response?.data?.message || "Something went wrong." };
  }
};

/**
 * Fetch files list and their upload/parsed status
 * @param {AbortController} signal AbortController signal
 */
export const mlExcelUploadData = async (signal) => {
  try {
    const resp = await axios.get(`${API_URL}/mlexceluploaddata/`, { signal });
    return { success: resp.data.message === "Success", ...resp.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      cancelled: axios.isCancel(e),
    };
  }
};
